import { useCallback, useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons/faArrowsRotate';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';

import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';

import Global from '../../../../store/global-selectors';
import { refreshAllStores } from '../../../../store/seller/seller.actions';
import { saveUserSetting } from '../../../../store/user/user.actions';
import { UserSettings } from '../../../../enums/user-settings/user-settings';
import { useModal } from '../../../../hooks/use-modal';

import type { CardType } from './types';
import { OperationsCard } from './operations-card';
import { OperationsCardSkeleton } from './operations-card-skeleton';
import { useSelectedCardTypes } from './hooks/use-selected-card-types';
import { EditCardsModal } from './components/edit-cards-modal/edit-cards-modal';
import { OperationsCardsDataProvider, useOperationsCardsData } from './contexts/operations-cards-data-context';
import { messages } from './messages';
import style from './operations-cards.module.less';

type Store = {
  lastRefreshAttemptedDate?: string;
};

const OperationsCardsContent = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const stores = useSelector(Global.selectSellerStores);
  const loadingStoreIds = useSelector(Global.selectSellerLoadingStoreIds);
  const selectedCardTypes = useSelectedCardTypes();
  const { isOpen: isModalOpen, open: openModal, close: closeModal } = useModal();
  const { isLoadingStatusCounts } = useOperationsCardsData();

  const [isManuallySpinning, setIsManuallySpinning] = useState(false);

  const isSyncing = loadingStoreIds.length > 0 || isManuallySpinning;

  // Initialize last sync time from stores on mount
  const [lastSyncTime, setLastSyncTime] = useState<DateTime | null>(() =>
    stores
      ?.filter((store: Store) => store.lastRefreshAttemptedDate)
      .reduce(
        (latest: DateTime | null, store: Store) => {
          const storeDate = DateTime.fromISO(store.lastRefreshAttemptedDate!);
          if (!storeDate.isValid) {
            return latest;
          }
          return !latest || storeDate > latest ? storeDate : latest;
        },
        null as DateTime | null,
      ),
  );

  // Track when sync completes and update to current time
  const wasSyncingRef = useRef(false);

  useEffect(() => {
    if (wasSyncingRef.current && !isSyncing) {
      setLastSyncTime(DateTime.now());
    }
    wasSyncingRef.current = isSyncing;
  }, [isSyncing]);

  const handleSyncOrders = useCallback(() => {
    dispatch(refreshAllStores());
    setIsManuallySpinning(true);
    setTimeout(() => {
      setIsManuallySpinning(false);
    }, 2000);
  }, [dispatch]);

  const handleSaveCards = useCallback(
    (newSelectedCards: CardType[]) => {
      dispatch(
        saveUserSetting({
          settingName: UserSettings.operationsSnapshotSelectedCards.settingName,
          settingValue: newSelectedCards,
          excludeV3prefix: true,
        }),
      );
      closeModal();
    },
    [dispatch, closeModal],
  );

  return (
    <section className={style['operations-section']}>
      <FlexRow className={style['operations-header']}>
        <Text textType={TextType.H2} value={intl.formatMessage(messages.sectionTitle)} />
        <FlexRow gap={Gap.Space8}>
          {lastSyncTime && (
            <Text
              className={style['operations-last-sync']}
              textType={TextType.H4}
              value={intl.formatMessage(messages.lastSync, { time: lastSyncTime.toFormat('h:mma') })}
            />
          )}
          {lastSyncTime && <div className={style['operations-divider']} />}
          <Button
            buttonSize='small'
            className={`${style['operations-sync-button']} ${isSyncing ? style['is-syncing'] : ''}`}
            onClick={handleSyncOrders}
            theme={THEME_CONSTANTS.unstyled}
          >
            <span className={style['operations-sync-icon-wrapper']}>
              <FontAwesomeIcon icon={faArrowsRotate} />
            </span>
            {intl.formatMessage(messages.syncOrders)}
          </Button>
          <Button
            ariaLabel={intl.formatMessage(messages.editCards)}
            buttonSize='small'
            onClick={openModal}
            theme={THEME_CONSTANTS.tertiary}
          >
            <FontAwesomeIcon icon={faPen} />
            {intl.formatMessage(messages.edit)}
          </Button>
        </FlexRow>
      </FlexRow>

      <FlexRow className={style['operations-cards-row']} gap={Gap.Space16}>
        {isLoadingStatusCounts
          ? selectedCardTypes.map((cardType) => <OperationsCardSkeleton key={cardType} />)
          : selectedCardTypes.map((cardType) => <OperationsCard key={cardType} cardType={cardType} />)}
      </FlexRow>

      {isModalOpen && <EditCardsModal initialSelectedCards={selectedCardTypes} onClose={closeModal} onSave={handleSaveCards} />}
    </section>
  );
};

export const OperationsCards = () => {
  const selectedCardTypes = useSelectedCardTypes();

  return (
    <OperationsCardsDataProvider activeCardTypes={selectedCardTypes}>
      <OperationsCardsContent />
    </OperationsCardsDataProvider>
  );
};
